import { showToast } from '@lib/utils/toastmaster';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';

// Backend pack size, in raw API credits. Callers format `topUpCreditsApi`
// via `formatCredits` so the display value follows the display scale.
const CREDITS_PER_PACK_API = 250_000;
const PRICE_PER_PACK = 20;
const MAX_SELF_SERVE_PACKS = 5;

/**
 * Computes top-up details for a credit deficit.
 * Returns null if no top-up is needed (i.e. credits are sufficient).
 */
export function computeTopUpDetails(creditsCost: number, creditsAvailable: number) {
  const extraCreditsNeeded = creditsCost - creditsAvailable;
  if (extraCreditsNeeded <= 0) return null;
  const quantity = Math.ceil(extraCreditsNeeded / CREDITS_PER_PACK_API);
  const topUpCreditsApi = quantity * CREDITS_PER_PACK_API;
  const topUpPrice = quantity * PRICE_PER_PACK;
  const shouldContactSales = quantity > MAX_SELF_SERVE_PACKS;
  return { quantity, topUpCreditsApi, topUpPrice, shouldContactSales };
}

/**
 * Where a billing action's `redirectUrl` (Stripe checkout or portal) opens.
 *
 * `new-tab` is the in-app default: the user has work on screen worth keeping. `same-tab` is
 * for the paywall, where there is nothing to protect and the page the user comes back to
 * must be the one that notices the payment (see `paywallStore.checkout`).
 */
export type BillingRedirectTarget = 'new-tab' | 'same-tab' | 'none';

type BillingActionOptions = {
  openIn?: BillingRedirectTarget;
  /** Refetch billing once the action resolved. Skipped for `same-tab`: the page is leaving. */
  refetchCredits?: boolean;
  /** Toastmaster template shown when the action fails. */
  errorToast?: 'credits-refresh-error' | 'subscription-checkout-error';
};

/**
 * Executes a billing action with consistent error handling
 * @param action - The async action to execute
 * @param actionName - Descriptive name for logging purposes
 * @throws Re-throws error after logging so callers can handle
 */
const executeBillingAction = async (
  action: () => Promise<{ redirectUrl?: string } | null | undefined>,
  actionName: string,
  {
    openIn = 'new-tab',
    refetchCredits = true,
    errorToast = 'credits-refresh-error',
  }: BillingActionOptions = {},
): Promise<void> => {
  // Pre-open window synchronously so we're still in the user-gesture call
  // stack. iOS Safari blocks window.open called after an await.
  let newWindow = openIn === 'new-tab' ? window.open('about:blank', '_blank') : null;
  if (newWindow) {
    newWindow.opener = null;
  }

  try {
    const result = await action();
    if (result?.redirectUrl && newWindow) {
      newWindow.location.href = result.redirectUrl;
    } else {
      newWindow?.close();
    }
    // Clear reference so we don't close an already-navigated window on error
    newWindow = null;
    if (result?.redirectUrl && openIn === 'same-tab') {
      window.location.assign(result.redirectUrl);
      return;
    }
    if (refetchCredits) {
      await billingStore.refetchCreditsAfterOperation();
    }
  } catch (error) {
    newWindow?.close();

    // Show user-friendly error toast
    showToast(errorToast, { error });

    throw error;
  }
};

/**
 * Handles a credit-pack purchase — opens the purchase URL in a new tab and
 * refreshes billing data afterwards. `count` is the number of packs (the
 * pack size is whatever the backend's one-time credits product currently
 * resolves to; see CREDITS_PER_PACK_API).
 */
export const handleCreditsPurchase = async (count: number = 1): Promise<void> => {
  await executeBillingAction(() => billingStore.purchase300kCredits(count), 'purchasing credits');
  trackingStore.creditsPurchased();
};

/**
 * Handles the purchase of a one-time product
 * Opens the purchase URL in a new tab and refreshes billing data
 * @param productId - The ID of the one-time product to purchase
 */
export const handleOneTimeProductPurchase = async (productId: string): Promise<void> => {
  await executeBillingAction(
    () => billingStore.purchaseOneTimeProduct(productId),
    'purchasing one-time product',
  );
};

/**
 * Handles opening the billing portal
 * Opens the portal URL in a new tab
 * @param source - Which surface opened it, for analytics
 */
export const handlePortalClick = async (source?: string): Promise<void> => {
  await executeBillingAction(
    async () => {
      const portalUrl = await billingStore.getPortalUrl();
      return portalUrl ? { redirectUrl: portalUrl } : null;
    },
    'opening portal',
    { refetchCredits: false },
  );
  trackingStore.billingPortalOpened(source ? { source } : undefined);
};

/**
 * Handles subscription changes. Made in place for a paid plan; a plan granted without a card is
 * replaced through Stripe Checkout, which comes back as a redirect and opens in this tab so the
 * payment return page notices the new plan, as for a first purchase.
 * FIXME: inside the landing page's embed (`?embed=1`) Stripe refuses to load in the iframe, so
 * that redirect needs the pre-opened new tab `handleSubscriptionPurchase` uses; left as is until
 * a user on such a plan changes it from the embed.
 * @param productId - The ID of the product to change to
 */
export const handleSubscriptionChange = async (productId: string): Promise<void> => {
  await executeBillingAction(
    async () => (await billingStore.changeSubscription(productId))?.checkout,
    'changing subscription',
    {
      openIn: 'same-tab',
    },
  );
};

/**
 * Handles subscription purchases
 * @param productId - The ID of the product to purchase
 * @param openIn - Where Stripe checkout opens; see `BillingRedirectTarget`
 * @returns Promise that resolves when purchase is initiated
 */
export const handleSubscriptionPurchase = async (
  productId: string,
  openIn: BillingRedirectTarget = 'new-tab',
): Promise<void> => {
  await executeBillingAction(
    () => billingStore.purchaseSubscription(productId),
    'purchasing subscription',
    { openIn, errorToast: 'subscription-checkout-error' },
  );
};
